//@flow
import { call, put, takeLatest, select } from "redux-saga/effects";
import moment from "moment-timezone";
import TimeZone from "react-native-timezone";

import { Platform } from "react-native";
import setCrashAnalytics from "@microdoing/shared/utils/crashlytics";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { getActiveCoachingSessionRequest } from "@microdoing/shared/services/coaching/actions";
import {
  getUserId,
  getUserProfilePictureUrl,
} from "@microdoing/shared/services/user/selectors";
import {
  SIGNUP_REQUESTING,
  SIGNUP_SUCCESS,
  SIGNUP_ERROR,
  SIGNUP_ONBOARDING_END_REQUESTING,
  GET_CLIENT_SUCCESS,
  GET_CLIENT_ERROR,
  CHECK_MAGIC_CODE_SUCCESS,
  CHECK_MAGIC_CODE_ERROR,
  CHECK_MAGIC_CODE_REQUESTING,
} from "./constants";
// import crashlytics from "@react-native-firebase/crashlytics";
import { fetchApi } from "../api";
import { setToken } from "../token/actions";
import { navigate, reset } from "../navigation/navigatorHelper";
import { modifyUser, setUser } from "../user/actions";
import socket from "../socket";
import i18n from "../../i18n";
import { getClientRequest } from "../client/actions";
import { getCurrentUserCampaignRequest } from "../userCampaign/actions";
import { getOnboardingSurveyRequest } from "../survey/actions";

const endPoints = {
  signup: "/v1/auth/register",
  onboardingEnd: "/v1/users/onboarding-end",
  getClient: "/v1/clients/", // :clientId
  checkMagicCode: "/v1/auth/magic-code/check",
};

type signupData = {
  email: string;
  name: string;
  password: string;
};

const getClientApi = data => {
  return fetchApi(`${endPoints.getClient}/${data}`, {}, "get");
};

const signupApi = (data: signupData) => {
  return fetchApi(endPoints.signup, data, "post");
};

const checkMagicCodeApi = (data: signupData) => {
  return fetchApi(endPoints.checkMagicCode, data, "post");
};

const onboardingEndApi = (data: signupData) => {
  return fetchApi(endPoints.onboardingEnd, data, "post");
};

function* checkMagicCodeFlow(action) {
  try {
    const response = yield call(checkMagicCodeApi, action.payload);

    yield put({ type: CHECK_MAGIC_CODE_SUCCESS, payload: response });

    navigate("SignupCreateAccount");
    yield put(
      getOnboardingSurveyRequest({ accessCode: action.payload.accessCode }),
    );
  } catch (e) {
    console.log(e);
    yield put({ type: CHECK_MAGIC_CODE_ERROR, payload: e.message });
  }
}

function* signupFlow(action) {
  try {
    const data = action.payload;

    // Timezone added
    data.timezone = moment.tz.guess();
    const response = yield call(signupApi, data);
    const { user, token } = response;
    yield put(setToken(token));
    const userId = user._id || user.id;
    yield put(setUser(user));

    // init token
    socket.emit("init", userId);
    yield put({ type: SIGNUP_SUCCESS });
    yield put(getClientRequest(user.client));
    navigate("SignupSetupName");
  } catch (error) {
    console.log(error);
    if (error.message === "ClientNotFound") {
      yield put({
        type: SIGNUP_ERROR,
        error: {
          name: error.message,
          field: "email",
          message:
            "Votre email pro n'appartient pas à la liste de nos companies clientes 👇",
        },
      });
    } else {
      yield put({
        type: SIGNUP_ERROR,
        error: {
          name: error.message,
          field: "email",
          message: "",
          // message: 'Il existe déjà un compte associé à cette adresse mail'
        },
      });
    }
  }
}

function* signupOnboardingEndFlow(action) {
  try {
    const signup = yield select(state => state.signup);
    const userId = yield select(getUserId);
    const userPicture = yield select(getUserProfilePictureUrl);

    const timezone = yield Platform.OS === "web"
      ? moment.tz.guess()
      : TimeZone.getTimeZone().then(zone => zone);

    const language = yield select(getLanguage);
    const { locale } = i18n.locale;
    const deviceOs = Platform.OS === "ios" ? "ios" : "android";
    const {
      sex,
      jobFunction,
      birthDate,
      firstName,
      lastName,
      manager,
      userTopicChoices,
      choice,
      feelingEvolution,
      mbti,
      phoneNumber,
      stats,
      goalNumber,
    } = signup;

    const payloadObject = {
      sex,
      deviceOs,
      jobFunction,
      birthDate,
      firstName,
      lastName,
      manager,
      topicChoices: userTopicChoices,
      choice,
      feelingEvolution,
      id: userId,
      timezone,
      language,
      locale,
      mbti,
      phoneNumber,
      picture: userPicture,
      stats,
      goalNumber,
    };

    const response = yield call(onboardingEndApi, payloadObject);
    yield put(modifyUser(response));

    yield put(getCurrentUserCampaignRequest({ userId }));
    yield put(getActiveCoachingSessionRequest({ userId }));

    socket.emit("init", userId);
    reset({
      index: 0,
      routes: [{ name: "NewCampaignModal" }],
    });

    yield put({ type: SIGNUP_SUCCESS });

    setCrashAnalytics("User SIGN UP", {
      email: response.email,
      userId,
      deviceOs,
    });
  } catch (error) {
    console.log(error);
    yield put({ type: SIGNUP_ERROR, error });
  }
}

function* signupWatcher() {
  // takeLatest() takes the LATEST call of that action and runs it
  // if we we're to use takeEvery, it would take every single
  // one of the actions and kick off a new task to handle it
  // CONCURRENTLY!!!
  yield takeLatest(SIGNUP_REQUESTING, signupFlow);
  yield takeLatest(SIGNUP_ONBOARDING_END_REQUESTING, signupOnboardingEndFlow);
  yield takeLatest(CHECK_MAGIC_CODE_REQUESTING, checkMagicCodeFlow);
}

export default signupWatcher;
